import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui";

/**
 * Catches uncaught render errors anywhere below it and shows a recovery
 * screen instead of a blank page. Without this, a single thrown error
 * in any component blanks the whole app — the user sees nothing and
 * has no obvious way to recover.
 *
 * Recovery: clicking "Reload" calls `window.location.reload()`. The
 * "Try again" button just resets the boundary state, useful when the
 * error was transient (a fetch race).
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to console with the full stack so devs can find it; in
    // production this is the AI's main signal that something blew up.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Render error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-8">
          <div className="max-w-md text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/15 text-rose-300">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <h1 className="mt-4 text-lg font-semibold text-zinc-100">Something broke</h1>
            <p className="mt-2 text-sm text-zinc-400">
              The page hit an unexpected error and couldn't render.
            </p>
            <pre className="mt-4 max-h-48 overflow-auto rounded-md border border-zinc-800 bg-zinc-900/60 p-3 text-left text-[11px] text-zinc-400">
              {this.state.error.message}
            </pre>
            <div className="mt-5 flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                onClick={() => this.setState({ error: null })}
              >
                Try again
              </Button>
              <Button
                size="sm"
                onClick={() => window.location.reload()}
              >
                Reload page
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
